import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Star, Shield, Phone, Mail, Globe, MessageSquare, ChevronRight, Share2, Flag, Check, Clock, ArrowLeft } from "lucide-react";
import { http, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

function StarRating({ value, onChange, size = 20 }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={() => onChange?.(n)} onMouseEnter={() => onChange && setHover(n)} onMouseLeave={() => onChange && setHover(0)}
          className={`transition-colors ${n <= (hover || value) ? "text-yellow-400" : "text-gray-200"}`}
          style={{ cursor: onChange ? "pointer" : "default" }}>
          <Star size={size} className={n <= (hover || value) ? "fill-yellow-400" : ""} />
        </button>
      ))}
    </div>
  );
}

export default function BusinessProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("about");
  const [showInquiry, setShowInquiry] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [inquiry, setInquiry] = useState({ name: user?.name || "", email: user?.email || "", message: "", phone: "" });
  const [review, setReview] = useState({ rating: 5, body: "" });
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    setLoading(true);
    Promise.all([
      http.get(`/marketplace/listings/${id}`).then(r => setListing(r.data)).catch(() => {}),
      http.get(`/marketplace/listings/${id}/reviews`).then(r => setReviews(r.data)).catch(() => {}),
      http.get("/marketplace/settings").then(r => setSettings(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="animate-pulse space-y-6">
      <div className="h-48 rounded-2xl bg-gray-100" />
      <div className="h-8 rounded bg-gray-100 w-1/3" />
      <div className="h-32 rounded-2xl bg-gray-100" />
    </div>
  );

  if (!listing) return (
    <div className="text-center py-20">
      <div className="text-lg font-semibold text-gray-500">Listing not found</div>
      <Link to="/marketplace" className="mt-4 inline-block text-sm font-semibold text-yellow-600">← Browse Services</Link>
    </div>
  );

  const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1) : null;

  const submitInquiry = async () => {
    if (!inquiry.message.trim()) { toast.error("Please enter a message"); return; }
    setSubmitting(true);
    try {
      await http.post(`/marketplace/listings/${id}/inquiry`, inquiry);
      toast.success("Inquiry sent! The business will contact you soon.");
      setShowInquiry(false);
      setInquiry(i => ({ ...i, message: "" }));
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSubmitting(false); }
  };

  const submitReview = async () => {
    if (!review.body.trim()) { toast.error("Please write your review"); return; }
    setSubmitting(true);
    try {
      await http.post(`/marketplace/listings/${id}/reviews`, review);
      toast.success("Review submitted!");
      setShowReview(false);
      http.get(`/marketplace/listings/${id}/reviews`).then(r => setReviews(r.data)).catch(() => {});
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSubmitting(false); }
  };

  const TABS = ["about", "services", "reviews"];
  if (settings.gallery_enabled !== false && listing.gallery?.length > 0) TABS.splice(2, 0, "gallery");

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Back */}
      <Link to="/marketplace" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-yellow-600 transition-colors">
        <ArrowLeft size={15} /> All Services
      </Link>

      {/* Cover */}
      <div className="relative rounded-3xl overflow-hidden h-48 md:h-64 bg-gradient-to-br from-gray-100 to-gray-200">
        {listing.cover_image
          ? <img src={listing.cover_image} alt={listing.name} className="w-full h-full object-cover" />
          : <div className="w-full h-full" style={{ background: "linear-gradient(135deg, #0F1E38 0%, #2a456c 100%)" }} />
        }
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        {listing.featured && <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold text-white bg-yellow-500">⭐ Featured</span>}
        {listing.verified && <span className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold text-blue-700 bg-white border border-blue-200 flex items-center gap-1"><Shield size={12} />Verified</span>}
      </div>

      {/* Profile header */}
      <div className="flex items-start gap-4 -mt-8 px-2 relative z-10">
        <div className="w-20 h-20 rounded-2xl overflow-hidden bg-white border-4 border-white shadow-lg shrink-0">
          {listing.logo_url
            ? <img src={listing.logo_url} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full grid place-items-center text-2xl font-bold" style={{ background: "#0F1E38", color: "#C5A028" }}>{(listing.name || "?")[0]}</div>
          }
        </div>
        <div className="flex-1 min-w-0 pt-8">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 leading-tight" style={{ fontFamily: "Cormorant Garamond, serif" }}>{listing.name}</h1>
              {listing.category_name && (
                <span className="inline-block text-xs font-bold text-yellow-700 bg-yellow-50 px-2.5 py-1 rounded-full mt-1">{listing.category_name}</span>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigator.share?.({ title: listing.name, url: window.location.href })} className="p-2.5 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50"><Share2 size={16} /></button>
            </div>
          </div>
          {listing.tagline && <p className="text-gray-600 mt-2 leading-relaxed">{listing.tagline}</p>}
          <div className="flex items-center gap-4 mt-2 flex-wrap text-sm text-gray-500">
            {listing.location && <span className="flex items-center gap-1.5"><MapPin size={14} className="text-yellow-600" />{listing.location}</span>}
            {avgRating && <span className="flex items-center gap-1.5"><Star size={14} className="text-yellow-400 fill-yellow-400" />{avgRating} ({reviews.length} reviews)</span>}
            {listing.response_time && <span className="flex items-center gap-1.5"><Clock size={14} />{listing.response_time}</span>}
            {listing.member_since && <span>Member since {new Date(listing.member_since).getFullYear()}</span>}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Left: Tabs */}
        <div className="md:col-span-2 space-y-4">
          <div className="flex gap-4 border-b border-gray-100">
            {TABS.map(t => (
              <button key={t} onClick={() => setTab(t)} className={`pb-3 text-sm font-semibold capitalize border-b-2 transition-colors ${tab === t ? "border-yellow-500 text-yellow-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>{t}</button>
            ))}
          </div>

          {tab === "about" && (
            <div className="space-y-4">
              <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">
                {listing.description || "No description provided."}
              </div>
              {listing.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {listing.tags.map((tag, i) => <span key={i} className="text-xs px-3 py-1 rounded-full bg-gray-100 text-gray-600">{tag}</span>)}
                </div>
              )}
            </div>
          )}

          {tab === "services" && (
            <div className="space-y-3">
              {(listing.service_list || listing.services || []).map((s, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-100">
                  <Check size={16} className="text-green-500 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">{typeof s === "string" ? s : s.name}</div>
                    {typeof s === "object" && s.description && <div className="text-sm text-gray-500 mt-0.5">{s.description}</div>}
                    {typeof s === "object" && s.price && <div className="text-sm font-bold text-yellow-700 mt-1">{s.price}</div>}
                  </div>
                </div>
              ))}
              {!(listing.service_list || listing.services)?.length && <div className="text-sm text-gray-500">No services listed yet.</div>}
            </div>
          )}

          {tab === "gallery" && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(listing.gallery || []).map((url, i) => (
                <div key={i} className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                  <img src={url} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300 cursor-pointer" />
                </div>
              ))}
            </div>
          )}

          {tab === "reviews" && (
            <div className="space-y-4">
              {avgRating && (
                <div className="flex items-center gap-4 p-4 bg-yellow-50 border border-yellow-100 rounded-2xl">
                  <div className="text-4xl font-bold text-gray-900">{avgRating}</div>
                  <div>
                    <StarRating value={Math.round(Number(avgRating))} size={18} />
                    <div className="text-sm text-gray-500 mt-1">{reviews.length} review{reviews.length !== 1 ? "s" : ""}</div>
                  </div>
                </div>
              )}
              {user && settings.reviews_enabled !== false && (
                <button onClick={() => setShowReview(true)} className="w-full py-3 rounded-xl border-2 border-dashed border-gray-200 text-sm font-semibold text-gray-500 hover:border-yellow-300 hover:text-yellow-600 transition-colors">
                  + Write a Review
                </button>
              )}
              {reviews.length === 0 && <div className="text-sm text-gray-500 text-center py-6">No reviews yet. Be the first!</div>}
              {reviews.map(r => (
                <div key={r.id} className="p-4 bg-white rounded-xl border border-gray-100">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="font-semibold text-sm text-gray-900">{r.reviewer_name || "Anonymous"}</span>
                      <div className="mt-0.5"><StarRating value={r.rating} size={14} /></div>
                    </div>
                    <span className="text-xs text-gray-400">{new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{r.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Contact card */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4 sticky top-24">
            <div className="font-bold text-gray-900">Contact {listing.name}</div>
            {listing.phone && (
              <a href={`tel:${listing.phone}`} className="flex items-center gap-3 text-sm text-gray-700 hover:text-yellow-600 transition-colors">
                <Phone size={16} className="text-yellow-600" />{listing.phone}
              </a>
            )}
            {listing.email && (
              <a href={`mailto:${listing.email}`} className="flex items-center gap-3 text-sm text-gray-700 hover:text-yellow-600 transition-colors">
                <Mail size={16} className="text-yellow-600" />{listing.email}
              </a>
            )}
            {listing.website && (
              <a href={listing.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-blue-600 hover:text-blue-700 transition-colors">
                <Globe size={16} />{listing.website.replace(/^https?:\/\//, "")}
              </a>
            )}
            {listing.whatsapp && (
              <a href={`https://wa.me/${listing.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-green-700 font-semibold bg-green-50 px-3 py-2.5 rounded-xl hover:bg-green-100 transition-colors">
                <MessageSquare size={16} /> WhatsApp
              </a>
            )}
            <button
              onClick={() => { if (!user) { navigate("/auth"); return; } setShowInquiry(true); }}
              className="w-full py-3 rounded-xl font-bold text-white text-sm flex items-center justify-center gap-2"
              style={{ background: "#0F1E38" }}
            >
              <MessageSquare size={16} /> Send Inquiry
            </button>
            {listing.availability && (
              <div className="text-xs text-center text-gray-500">{listing.availability}</div>
            )}
          </div>
        </div>
      </div>

      {/* Inquiry modal */}
      {showInquiry && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl p-6 space-y-4">
            <div className="flex items-center justify-between"><h3 className="font-bold text-gray-900">Send Inquiry</h3><button onClick={() => setShowInquiry(false)} className="p-1 text-gray-400 hover:text-gray-600"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg></button></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold text-gray-500 uppercase block mb-1">Your Name</label><input value={inquiry.name} onChange={e => setInquiry(i => ({ ...i, name: e.target.value }))} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none" /></div>
              <div><label className="text-xs font-bold text-gray-500 uppercase block mb-1">Phone</label><input value={inquiry.phone} onChange={e => setInquiry(i => ({ ...i, phone: e.target.value }))} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none" /></div>
            </div>
            <div><label className="text-xs font-bold text-gray-500 uppercase block mb-1">Email</label><input type="email" value={inquiry.email} onChange={e => setInquiry(i => ({ ...i, email: e.target.value }))} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none" /></div>
            <div><label className="text-xs font-bold text-gray-500 uppercase block mb-1">Message *</label><textarea value={inquiry.message} onChange={e => setInquiry(i => ({ ...i, message: e.target.value }))} rows={4} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none resize-none" placeholder="Describe what you need…" /></div>
            <div className="flex gap-3">
              <button onClick={() => setShowInquiry(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold">Cancel</button>
              <button onClick={submitInquiry} disabled={submitting} className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm disabled:opacity-50" style={{ background: "#0F1E38" }}>{submitting ? "Sending…" : "Send"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Review modal */}
      {showReview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl p-6 space-y-4">
            <div className="flex items-center justify-between"><h3 className="font-bold text-gray-900">Write a Review</h3><button onClick={() => setShowReview(false)} className="p-1 text-gray-400 hover:text-gray-600"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg></button></div>
            <div><label className="text-xs font-bold text-gray-500 uppercase block mb-2">Rating</label><StarRating value={review.rating} onChange={r => setReview(v => ({ ...v, rating: r }))}/></div>
            <div><label className="text-xs font-bold text-gray-500 uppercase block mb-1">Review</label><textarea value={review.body} onChange={e => setReview(v => ({ ...v, body: e.target.value }))} rows={4} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none resize-none" placeholder="Share your experience…" /></div>
            <div className="flex gap-3">
              <button onClick={() => setShowReview(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold">Cancel</button>
              <button onClick={submitReview} disabled={submitting} className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm disabled:opacity-50" style={{ background: "#0F1E38" }}>{submitting ? "Submitting…" : "Submit Review"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
